import Vue from 'vue'
import Router from 'vue-router'
import NProgress from 'nprogress'

import globalMixin from '@/mixins'
import store from '@/store'

import Settings from '@/layouts/SettingsLayout'
import SettingsTab from '@/layouts/SettingsTabs'
import Section from '@/layouts/SectionTabs'
import SubSection from '@/layouts/SubSection'

import error404 from '@/pages/error404'
import Login from '@/pages/Login'
import Verification from '@/pages/Verification'
import UserVerification from '@/pages/UserVerification'
import ResetPassword from '@/pages/ResetPassword'
import OTP from '@/pages/OTP'
import Dashboard from '@/pages/Dashboard'
import Reports from '@/pages/Reports'
import Invoices from '@/pages/Invoices'

import VAList from '@/pages/VA/List'
import VATransactions from '@/pages/VA/Transactions'
import VAInvalidCredits from '@/pages/VA/InvalidCredits'
import VAConnectedBanking from '@/pages/VA/ConnectedBanking'

import UPIList from '@/pages/UPI/List'
import UPITransactions from '@/pages/UPI/Transactions'
import UPICollections from '@/pages/UPI/Collections'
import UPIInvalidCredits from '@/pages/UPI/InvalidCredits'
import UPIConnectedBanking from '@/pages/UPI/ConnectedBanking'

import SettingsAccount from '@/pages/Settings/Account'
import SettingsApi from '@/pages/Settings/Api'
import SettingsWebhook from '@/pages/Settings/Webhook'
import SettingsUserProfile from '@/pages/Settings/UserProfile'

import LiveEvents from '@/pages/Status/Live'
import ListEvents from '@/pages/Status/List'
import BeneBankStatus from '@/pages/Status/Banks/Status'
import BeneBankHistory from '@/pages/Status/Banks/History'

import RolesList from '@/pages/Roles/List'
import RoleDetails from '@/pages/Roles/Details'

import UsersList from '@/pages/Users/List'
import UserDetails from '@/pages/Users/Details'

import ImportsList from '@/pages/Imports/List'
import ImportProcessedTxns from '@/pages/Imports/Transactions'
import ImportUnprocessedTxns from '@/pages/Imports/InvalidTransactions'

import Orders from '@/pages/PG/Orders'
import Refunds from '@/pages/PG/Refunds'

import UpiDetails from '@/components/UPIs/Details'
import VaDetails from '@/components/VirtualAccounts/Details'
import ImportDetails from '@/components/Imports/Details'

import firebase from 'firebase/app'
import 'firebase/analytics'

firebase.initializeApp(process.env.FIREBASE_CONFIG)
let analytics = null

const setAnalytics = () => {
  let pathName = location.pathname
  if (!(pathName.includes('reset_password') || pathName.includes('verification'))) {
    analytics = firebase.analytics()
  }
}
setAnalytics()

Vue.use(Router)

let router = new Router({
  mode: 'history',
  routes: [
    { path: '*', name: 'error404', component: error404, meta: { title: 'Not Found' } },
    { path: '/login', name: 'Login', component: Login, meta: { title: 'Login' } },
    { path: '/verification/:token', name: 'Verification', component: Verification, meta: { title: 'Verification' } },
    { path: '/user_verification/:token', name: 'UserVerification', component: UserVerification, meta: { title: 'Verification' } },
    { path: '/reset_password/:token', name: 'ResetPassword', component: ResetPassword, meta: { title: 'Reset Password' } },
    { path: '/otp', name: 'OTP', component: OTP, meta: { title: 'OTP' } },
    { path: '/otp/setup', name: 'OTPSetup', component: OTP, meta: { title: 'Setup 2 Factor Authentication' } },
    { path: '/dashboard', name: 'Dashboard', component: Dashboard, meta: {title: 'Dashboard'} },
    {
      path: '/virtual_accounts/:id/transactions',
      name: 'VADetails',
      component: SubSection,
      redirect: { name: 'VATransactions' },
      children: [
        { path: '/virtual_accounts/:id/transactions', name: 'VATransactions', components: { default: VaDetails, more: VATransactions }, meta: { title: 'Virtual Account - Transactions', parentRoute: 'VAList' } },
        { path: '/virtual_accounts/:id/invalid_credits', name: 'VAIdInvalidCredits', components: { default: VaDetails, more: VAInvalidCredits }, props: {more: route => ({vaId: route.params.id})}, meta: { title: 'Virtual Account - Invalid Credits', parentRoute: 'VAList' } },
        { path: '/virtual_accounts/:id/sub_virtual_accounts', name: 'SubVaList', components: { default: VaDetails, more: VAList }, props: {more: route => ({vaId: route.params.id})}, meta: { title: 'Virtual Accounts - Sub VA List', parentRoute: 'VAList' } }
      ]
    },
    {
      path: '/virtual_accounts',
      name: 'VA',
      component: Section,
      redirect: {name: 'VAList'},
      children: [
        { path: '/virtual_accounts', name: 'VAList', component: VAList, meta: {title: 'Virtual Accounts'} },
        { path: '/virtual_accounts/invalid_credits', name: 'VAInvalidCredits', component: VAInvalidCredits, meta: {title: 'Virtual Account - Invalid Credits'} },
        { path: '/virtual_accounts/connected_banking', name: 'VAConnectedBanking', component: VAConnectedBanking, meta: {title: 'Virtual Account - Connected Banking'} }
      ]
    },
    {
      path: '/upis',
      name: 'UPI',
      component: Section,
      redirect: {name: 'UPIList'},
      children: [
        { path: '/upis', name: 'UPIList', component: UPIList, meta: {title: 'UPIs'} },
        { path: '/upis/collections', name: 'UPICollections', component: UPICollections, meta: {title: 'UPI Collections'} },
        { path: '/upis/invalid_credits', name: 'UPIInvalidCredits', component: UPIInvalidCredits, meta: { title: 'UPI - Invalid Credits' } },
        { path: '/upis/connected_banking', name: 'UPIConnectedBanking', component: UPIConnectedBanking, meta: {title: 'UPI - Connected Banking'} }
      ]
    },
    {
      path: '/upis/:id/transactions',
      name: 'UPIDetails',
      component: SubSection,
      redirect: { name: 'UPITransactions' },
      children: [
        { path: '/upis/:id/transactions', name: 'UPITransactions', components: { default: UpiDetails, more: UPITransactions }, meta: { title: 'UPI - Transactions', parentRoute: 'UPIList' } }
      ]
    },
    { path: '/reports', name: 'Reports', component: Reports, meta: {title: 'Reports'} },
    {
      path: '/status',
      name: 'Status',
      component: Settings,
      redirect: {name: 'LiveEvents'},
      children: [
        { path: '/status/current', name: 'LiveEvents', component: LiveEvents, meta: {title: 'Current Status'} },
        { path: '/status/history', name: 'PastEvents', component: ListEvents, meta: {title: 'Events History'} },
        { path: '/status/upcoming', name: 'UpcomingEvents', component: ListEvents, meta: {title: 'Upcoming Events'} },
        {
          path: 'banks',
          name: 'Banks',
          component: SettingsTab,
          redirect: {name: 'BeneBankStatus'},
          children: [
            { path: '/status/banks', name: 'BeneBankStatus', component: BeneBankStatus, meta: {title: 'Beneficiary Banks Status'} },
            { path: 'history', name: 'BeneBankHistory', component: BeneBankHistory, meta: {title: 'Beneficiary Banks History'} }
          ]
        }
      ]
    },
    {
      path: '/settings',
      name: 'Settings',
      component: Settings,
      redirect: {name: 'AccountSettings'},
      children: [
        { path: '/settings/account', name: 'AccountSettings', component: SettingsAccount, meta: {title: 'Settings - Account'} },
        { path: '/settings/api', name: 'ApiSettings', component: SettingsApi, meta: {title: 'Settings - API'} },
        { path: '/settings/webhooks', name: 'WebhookSettings', component: SettingsWebhook, meta: {title: 'Settings - Webhooks'} },
        { path: '/settings/user_profile', name: 'UserProfileSettings', component: SettingsUserProfile, meta: {title: 'Settings - User Profile'} }
      ]
    },
    { path: '/invoices', name: 'Invoices', component: Invoices, meta: {title: 'Invoices'} },
    {
      path: '/access',
      name: 'UserManagement',
      component: Settings,
      redirect: {name: 'RolesList'},
      children: [
        { path: '/access/roles', name: 'RolesList', component: RolesList, meta: {title: 'Roles'} },
        { path: '/access/users', name: 'UsersList', component: UsersList, meta: {title: 'Users'} }
      ]
    },
    { path: '/access/roles/new', name: 'RoleCreate', component: RoleDetails, meta: { title: 'Role - Create', parentRoute: 'RolesList' } },
    { path: '/access/roles/:id', name: 'RoleDetails', component: RoleDetails, meta: { title: 'Role - Details', parentRoute: 'RolesList' } },
    { path: '/access/users/new', name: 'UserCreate', component: UserDetails, meta: { title: 'User - Create', parentRoute: 'UsersList' } },
    { path: '/access/users/:id', name: 'UserDetails', component: UserDetails, meta: { title: 'User - Details', parentRoute: 'UsersList' } },
    { path: '/bulk_imports', name: 'ImportsList', component: ImportsList, meta: { title: 'Bulk Imports' } },
    {
      path: '/bulk_imports/:id/processed_transactions',
      name: 'ImportDetails',
      component: SubSection,
      redirect: { name: 'ImportProcessedTxns' },
      children: [
        { path: '/bulk_imports/:id/processed_transactions', name: 'ImportProcessedTxns', components: { default: ImportDetails, more: ImportProcessedTxns }, meta: { title: 'Bulk Import - Processed Transactions', parentRoute: 'ImportsList' } },
        { path: '/bulk_imports/:id/unprocessed_transactions', name: 'ImportUnprocessedTxns', components: { default: ImportDetails, more: ImportUnprocessedTxns }, meta: { title: 'Bulk Import - Unprocessed Transactions', parentRoute: 'ImportsList' } }
      ]
    },
    {
      path: '/pg',
      name: 'PaymentGateway',
      component: Section,
      redirect: {name: 'Orders'},
      children: [
        { path: '/pg/orders', name: 'Orders', component: Orders, meta: {title: 'PG - Orders'} },
        { path: '/pg/refunds', name: 'Refunds', component: Refunds, meta: {title: 'PG - Refunds'} }
      ]
    }
  ]
})

let unSecuredRoutes = ['Login', 'Signup', 'Verification', 'ResetPassword', 'UserVerification']
let otpPages = ['OTP', 'OTPSetup']

const setTargetPage = (to, from, next, defaultRoute) => {
  let targetPage = localStorage.getItem('target_page')
  if (targetPage) {
    targetPage = atob(targetPage)

    if (targetPage === 'ANY' && otpPages.includes(to.name)) {
      next({name: 'Dashboard'})
    } else if (targetPage !== 'ANY') {
      let targetRoute = targetPage === 'OTP' ? 'OTP' : 'OTPSetup'

      if (targetRoute !== to.name) {
        store.commit('SET_TARGET_PAGE')
        store.commit('SET_USER_LOGGED_IN')
        next({name: targetRoute})
      } else {
        next()
      }
    } else {
      if (defaultRoute) {
        store.commit('SET_TARGET_PAGE')
        store.commit('SET_USER_LOGGED_IN')
        next({name: 'Dashboard'})
      } else {
        next()
      }
    }
  } else {
    if (otpPages.includes(to.name) || unSecuredRoutes.includes(to.name)) {
      next({name: 'Dashboard'})
    } else {
      next()
    }
  }
}

router.beforeEach((to, from, next) => {
  NProgress.configure({parent: '#app'})
  NProgress.start()
  document.title = 'Hypto Partner | ' + to.meta.title

  if (to.path === '/') {
    next({name: 'Login'})
  } else if (unSecuredRoutes.includes(to.name)) {
    if (localStorage.getItem('access_token')) {
      setTargetPage(to, from, next, true)
    } else {
      next()
    }
  } else {
    if (localStorage.getItem('access_token')) {
      setTargetPage(to, from, next)
    } else {
      globalMixin.methods.errorToastMsg('Please login to continue')
      store.commit('SET_USER_LOGGED_IN', false)
      next({name: 'Login'})
    }
  }

  NProgress.done()
})

router.afterEach((to, from) => {
  store.commit('SET_PREVIOUS_ROUTE', from.name)

  if (analytics) {
    analytics.logEvent('route_enter')
  } else {
    setAnalytics()
  }
})

export default router
